import { Package, Truck, MapPin, CheckCircle2, RotateCcw, XCircle, ExternalLink, Clock } from "lucide-react";

/**
 * Order shipment tracking timeline.
 * Currently driven by Order.order_status (placed/confirmed/packed/shipped/delivered/cancelled/returned).
 * Will be enriched with real Shiprocket events once the integration is wired.
 *
 * Props:
 *   - status: order_status string
 *   - courier: courier name (e.g. "Bluedart") — optional
 *   - awb: AWB / tracking number — optional
 *   - trackingUrl: external Shiprocket tracking URL — optional
 *   - events: optional list of {timestamp, label, location} from Shiprocket
 */
const STEPS = [
  { key: "placed",      label: "Order Placed",       icon: Package },
  { key: "packed",      label: "Shipment Created",   icon: Package },
  { key: "shipped",     label: "Picked Up",          icon: Truck },
  { key: "in_transit",  label: "In Transit",         icon: Truck },
  { key: "out_for_delivery", label: "Out for Delivery", icon: MapPin },
  { key: "delivered",   label: "Delivered",          icon: CheckCircle2 },
];

// Maps order_status → the timeline step considered "reached".
const STATUS_TO_STEP_INDEX = {
  placed: 0,
  confirmed: 0,
  packed: 1,
  shipped: 2,
  in_transit: 3,
  out_for_delivery: 4,
  delivered: 5,
};

const TERMINAL_NEGATIVE = {
  cancelled: { icon: XCircle, label: "Order Cancelled", color: "text-brand-chili", bg: "bg-brand-chili/10" },
  returned:  { icon: RotateCcw, label: "Order Returned", color: "text-brand-muted", bg: "bg-brand-subtle" },
};

export default function TrackingTimeline({ status = "placed", courier, awb, trackingUrl, events = [] }) {
  // Terminal negative states: render alternative panel
  if (TERMINAL_NEGATIVE[status]) {
    const t = TERMINAL_NEGATIVE[status];
    const Icon = t.icon;
    return (
      <div
        className={`rounded-2xl p-6 ${t.bg} border border-brand-line`}
        data-testid="tracking-terminal"
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-6 h-6 ${t.color}`} strokeWidth={1.5} />
          <div>
            <div className="font-serif text-xl text-brand-dark">{t.label}</div>
            <div className="text-xs text-brand-muted mt-0.5">
              {status === "cancelled" ? "If amount was debited, refund will be processed in 5–7 days." : "Refund will be processed once the package reaches us."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentIdx = STATUS_TO_STEP_INDEX[status] ?? 0;
  const hasShipping = Boolean(courier || awb);

  return (
    <div className="bg-white border border-brand-line rounded-2xl p-6 sm:p-8" data-testid="tracking-timeline">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h3 className="font-serif text-xl text-brand-dark">Shipment tracking</h3>
          {!hasShipping && (
            <p className="text-xs text-brand-muted mt-1">
              Tracking details will appear here once your order is dispatched.
            </p>
          )}
        </div>
        {trackingUrl && (
          <a
            href={trackingUrl}
            target="_blank"
            rel="noreferrer"
            className="btn-primary !py-2.5 !px-5 !text-sm"
            data-testid="track-order-btn"
          >
            Track order <ExternalLink className="ml-2 w-3.5 h-3.5" strokeWidth={2} />
          </a>
        )}
      </div>

      {hasShipping && (
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-y-4 gap-x-6 mb-7 pb-7 border-b border-brand-line">
          <div>
            <dt className="overline !text-brand-muted text-[10px] mb-1">Courier Partner</dt>
            <dd className="text-sm text-brand-dark" data-testid="track-courier">{courier || "—"}</dd>
          </div>
          <div>
            <dt className="overline !text-brand-muted text-[10px] mb-1">AWB / Tracking #</dt>
            <dd className="text-sm text-brand-dark font-mono" data-testid="track-awb">{awb || "—"}</dd>
          </div>
          <div>
            <dt className="overline !text-brand-muted text-[10px] mb-1">Live Status</dt>
            <dd className="text-sm text-brand-dark capitalize" data-testid="track-status">
              {String(status).replace(/_/g, " ")}
            </dd>
          </div>
        </dl>
      )}

      <ol className="relative pl-6 space-y-6" data-testid="track-steps">
        <span className="absolute left-[7px] top-1 bottom-1 w-px bg-brand-line" aria-hidden="true" />
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const reached = i <= currentIdx;
          return (
            <li key={s.key} className="relative" data-testid={`track-step-${s.key}`}>
              <span
                className={`absolute -left-[18px] top-0 w-4 h-4 rounded-full flex items-center justify-center ${
                  reached ? "bg-brand-chili" : "bg-brand-line"
                }`}
                aria-hidden="true"
              >
                {reached && <Icon className="w-2.5 h-2.5 text-white" strokeWidth={2.5} />}
              </span>
              <div className={`text-sm font-medium ${reached ? "text-brand-dark" : "text-brand-muted"}`}>
                {s.label}
              </div>
              {reached && i === currentIdx && (
                <div className="text-xs text-brand-muted mt-0.5 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" strokeWidth={1.5} /> Current status
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {events.length > 0 && (
        <details className="mt-8 group" data-testid="track-event-history">
          <summary className="cursor-pointer text-sm font-medium text-brand-chili">
            Full courier history ({events.length} updates)
          </summary>
          <ul className="mt-4 space-y-3 pl-2 border-l border-brand-line">
            {events.map((ev, idx) => (
              <li key={`${ev.timestamp || ev.label || "evt"}-${idx}`} className="pl-4 relative" data-testid={`track-event-${idx}`}>
                <span className="absolute left-[-5px] top-1.5 w-2.5 h-2.5 rounded-full bg-brand-line" aria-hidden="true" />
                <div className="text-sm text-brand-dark">{ev.label}</div>
                {ev.location && <div className="text-xs text-brand-muted">{ev.location}</div>}
                {ev.timestamp && <div className="text-xs text-brand-muted mt-0.5">{new Date(ev.timestamp).toLocaleString("en-IN")}</div>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
